import { Container, Group, Grid, GridCol, Accordion, Flex, Title, ScrollArea, Button, Text } from '@mantine/core';
import { useSelector } from 'react-redux';
import { ICurrency } from '../../../../libraries/currencies';
import { Transaction, Values } from '../../../../libraries/transactions';
import { AppState } from '../../../client-redux/reducers';
import ModalComponent from '../../../components/modal';
import DisplayField from '../../../components/display-field';
import ProductsTable from '../../../components/products-table';
import { Language } from '../../../languages/languageHandler';
import { formatDateTimeLabel } from '../../../utils/date';
import {
  formatCurrAmount,
  getLocationName,
  formatTransactionMethod,
  formatTransactionType,
  formatDocTypeName
} from '../../../utils/script';
import { processTransactionData } from '../transaction-data';
import TransactionTable from '../transaction-table';
import { IconFileTypePdf, IconDownload } from '@tabler/icons-react';
import './styles.scss';

interface ShowTransactionProps {
  transaction: Transaction;
  openDetails: boolean;
  hideBtn: () => void;
  lang: Language;
  currency: ICurrency;
  getLog: () => void;
  getReport: () => void;
  isDeposit?: boolean;
}

const ShowTransaction = ({
  transaction,
  openDetails,
  hideBtn,
  currency,
  getLog,
  getReport,
  isDeposit
}: ShowTransactionProps) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const region = useSelector<AppState, string>(state => state.login.user?.region || 'UTC');
  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const matchingCurrency = currencies?.find(c => c.cid === transaction.cid);

  const processed = processTransactionData(transaction, currencies);
  const receivedBlocks = processed.filter(p => p.received);
  const dispensedBlocks = processed.filter(p => p.dispensed);

  if (transaction.equiType === 442) {
    isDeposit = true;
  }

  const formatMultiCurrency = (values: Values[], currencies: ICurrency[], type: 'rec' | 'dis') => {
    return values
      .map(v => {
        const cur = currencies.find(c => c.cid === v.cid);
        if (!cur) return null;

        const amount = type === 'rec' ? v.valueRec ?? 0 : v.valueDis ?? 0;

        return `${formatCurrAmount(amount, cur)}`;
      })
      .filter(Boolean)
      .join(' | ');
  };

  return (
    <>
      <ModalComponent
        show={openDetails}
        handleClick={hideBtn}
        title={`${lang.TRANSACTION} ${transaction.eid}-${transaction.period}-${transaction.code}`}
        content={
          <>
            <Container classNames={{ root: 'transaction-details' }}>
              <Group className="details">
                <Grid gap="sm">
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField title={lang.EQUIPMENT_ID} content={transaction.eid} />
                  </GridCol>
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField title={lang.EQUIPMENT_LOCAL} content={getLocationName(transaction.sid)} />
                  </GridCol>
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField title={lang.GLOBAL_PERIOD} content={transaction.period} />
                  </GridCol>
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField title={lang.GLASS_CODE} content={transaction.code} />
                  </GridCol>
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.GLOBAL_DATE_TIME}
                      content={formatDateTimeLabel(transaction.dateTime, region)}
                    />
                  </GridCol>
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.GLOBAL_AMOUNT}
                      content={matchingCurrency ? formatCurrAmount(transaction.value || 0, matchingCurrency) : '-'}
                    />
                  </GridCol>
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.TRANSACTION_RECEIVED}
                      content={formatMultiCurrency(transaction.values, currencies, 'rec') || '-'}
                    />
                  </GridCol>
                  {!isDeposit && (
                    <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                      <DisplayField
                        title={lang.TRANSACTION_DISPENSED}
                        content={formatMultiCurrency(transaction.values, currencies, 'dis') || '-'}
                      />
                    </GridCol>
                  )}

                  {/* display valueType / payment method if it is a payment transaction */}
                  {transaction.type.startsWith('010101') && (
                    <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                      <DisplayField
                        title={lang.TRANSACTION_PAYMENT_METHOD}
                        content={formatTransactionMethod(lang, transaction.valueType)}
                      />
                    </GridCol>
                  )}

                  {transaction.docCode && (
                    <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                      <DisplayField
                        title={lang.TRANSACTION_DOC}
                        content={`${formatDocTypeName(lang, transaction.docType)} / ${transaction.docCode}`}
                      />
                    </GridCol>
                  )}
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField title={lang.GLOBAL_TYPE} content={formatTransactionType(lang, transaction.type)} />
                  </GridCol>
                </Grid>
              </Group>

              <Accordion variant="filled" radius="md" mb="md">
                {transaction.bankingDetails && (
                  <Accordion.Item value="Banking Details">
                    <Accordion.Control>
                      <Flex justify="center">
                        <Title size="h4">{lang.TRANSACTION_BANKING_DETAILS}</Title>
                      </Flex>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Grid gap="md">
                        <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                          <DisplayField title={lang.USER_CARD} content={transaction.bankingDetails.card} />
                        </GridCol>
                        <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                          <DisplayField title={lang.GLOBAL_ACCOUNT} content={transaction.bankingDetails.account} />
                        </GridCol>
                        <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                          <DisplayField
                            title={lang.TRANSACTION_DEPOSITOR}
                            content={transaction.bankingDetails.depositor}
                          />
                        </GridCol>
                        <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                          <DisplayField title={lang.GLOBAL_REASON} content={transaction.bankingDetails.reason} />
                        </GridCol>
                        {transaction.bankingDetails.declaration && (
                          <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                            <DisplayField
                              title={lang.GLOBAL_DECLARATION}
                              content={transaction.bankingDetails.declaration}
                            />
                          </GridCol>
                        )}
                      </Grid>
                    </Accordion.Panel>
                  </Accordion.Item>
                )}

                {(transaction.readValues ?? []).length > 0 && currency && (
                  <Accordion.Item value={lang.CURRENCY_TABLE_READING} className="reading-details">
                    <Accordion.Control>
                      <Flex justify="center">
                        <Title size="h4">{lang.CURRENCY_TABLE_READING}</Title>
                      </Flex>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <ScrollArea h={400}>
                        <TransactionTable currency={currency} values={transaction.readValues} />
                      </ScrollArea>
                    </Accordion.Panel>
                  </Accordion.Item>
                )}
              </Accordion>

              {transaction.products && transaction.products.length > 0 && (
                <>
                  <Title>{lang.PRODUCT_DETAILS}</Title>
                  <ProductsTable lang={lang} products={transaction.products} />
                </>
              )}

              {receivedBlocks.length > 0 && (
                <>
                  <Title className="table-titles">{lang.CURRENCY_TABLE_CASH_IN}</Title>
                  {receivedBlocks.map((block, idx) => (
                    <TransactionTable key={`rec-${idx}`} currency={block.currency} cash={block.denominations} />
                  ))}
                </>
              )}

              {dispensedBlocks.length > 0 && (
                <>
                  <Title className="table-titles">{lang.CURRENCY_TABLE_CASH_OUT}</Title>
                  {dispensedBlocks.map((block, idx) => (
                    <TransactionTable key={`dis-${idx}`} currency={block.currency} cash={block.denominations} />
                  ))}
                </>
              )}

              <Group className="buttons">
                <Button radius="md" onClick={() => getLog()}>
                  <IconDownload stroke={2} />
                  Logs
                </Button>
                <Button radius="md" onClick={() => getReport()}>
                  <IconFileTypePdf stroke={2} />
                  Report
                </Button>
              </Group>

              {transaction.softVersion && (
                <Group className="footer-version">
                  <Text>{`${lang.EQUIPMENT_SOFTWARE_VERSION}: ${transaction.softVersion}`}</Text>
                </Group>
              )}
            </Container>
          </>
        }
      />
    </>
  );
};

export default ShowTransaction;
